import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { cloneDeep } from "lodash";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import { useFormContext } from "react-hook-form";
import BooleanSetting from "../../atoms/BooleanSetting";
import InputSetting from "../../atoms/InputSetting";
import SingleSelectDropdown from "../../atoms/SingleSelectDropdown";
import MultiSelectDropdown from "../../atoms/MultiSelectDropdown";
import NestedMetrics from "../NestedMetrics";

const componentTypeMap = {
  boolean: {
    Component: BooleanSetting,
  },
  number: {
    Component: InputSetting,
  },
  "single-select-dropdown": {
    Component: SingleSelectDropdown,
  },
  "multi-select-dropdown": {
    Component: MultiSelectDropdown,
  },
};

function NotificationBuilderSetting({ setting, setParentState }) {
  const { clearErrors } = useFormContext();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
      }}
    >
      {/* Loop through each field_value in notification setting */}
      {setting.field_value.map((notification, notificationIndex) => {
        // extract validation for the specific category
        const validations = setting?.metadata?.validations?.category[notification?.category];
        return (
          <Accordion
            sx={{
              width: "100%",
            }}
            key={notification?.description}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Grid container spacing={6}>
                <Grid item xs={1}>
                  <BooleanSetting
                    setting={{
                      field_value: notification.active,
                    }}
                    setParentState={(checked) => {
                      const copy = cloneDeep(setting.field_value);
                      copy[notificationIndex].active = checked;
                      setParentState(copy);
                    }}
                    styles={{
                      marginBottom: "1rem",
                    }}
                  />
                </Grid>
                <Grid item xs={11} container alignItems="center" justifyContent="flex-start">
                  <Typography variant="p">{notification.description}</Typography>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {/* Loop through each notificationRows for each notification category */}
                {notification.notificationRows.map((row, index) => {
                  // Check for metrics is array or not
                  const isArrayMatrix = Array.isArray(row.metrics);

                  // If it's array metrics then set empty array or else set Object entries for object metrics
                  let metricEntries = isArrayMatrix ? [] : Object.entries(row.metrics);

                  // Extract metrics validations from category validations
                  const metricsValidations = validations?.metrics;
                  return (
                    <Box key={`${notification.category}-${row.rowId}`}>
                      {/* display notificationRecipientType & recipientList if it exists in row */}
                      {"notificationRecipientType" in row && "recipientList" in row ? (
                        <>
                          <SingleSelectDropdown
                            name={`notificationRecipientType-${notification.category}-${row.rowId}`}
                            setting={{
                              field_value: row.notificationRecipientType,
                              en_field_label: "Recipient type",
                              options: notification.notificationRecipientTypeValues,
                            }}
                            setParentState={(newSelectedValue) => {
                              const copy = cloneDeep(setting.field_value);
                              copy[notificationIndex].notificationRows[
                                index
                              ].notificationRecipientType = {
                                display: newSelectedValue,
                                value: newSelectedValue,
                              };
                              copy[notificationIndex].notificationRows[index].recipientList = [];
                              setParentState(copy);
                            }}
                            styles={{
                              width: "100%",
                              marginBottom: "1.5rem",
                            }}
                            validations={validations?.recipientType || {}}
                          />
                          <MultiSelectDropdown
                            name={`recipientList-${notification.category}-${row.rowId}`}
                            setting={{
                              field_value: row.recipientList,
                              en_field_label: "Recipient List",
                              options:
                                notification.recipientListValues[
                                  row.notificationRecipientType.value
                                ],
                            }}
                            setParentState={(newSelectedValue) => {
                              const copy = cloneDeep(setting.field_value);
                              copy[notificationIndex].notificationRows[index].recipientList =
                                newSelectedValue;
                              setParentState(copy);
                            }}
                            styles={{
                              width: "100%",
                              marginBottom: "1.5rem",
                            }}
                            validations={{
                              validate: (value) =>
                                setting.field_value[notificationIndex].active &&
                                value?.length < validations?.recipientList?.length
                                  ? validations?.recipientList?.message
                                  : true,
                            }}
                          />
                        </>
                      ) : null}
                      {isArrayMatrix ? (
                        <Box>
                          {row.metrics.map((metric, i) => {
                            metricEntries = Object.entries(metric);
                            return (
                              <Grid
                                // eslint-disable-next-line react/no-array-index-key
                                key={`metric-${notification.category}-${row.rowId}-${i}`}
                                container
                                justifyContent="space-between"
                                sx={{
                                  width: "100%",
                                }}
                              >
                                {metricEntries
                                  .sort((metricEntryA, metricEntryB) => {
                                    const matchedMetricsA = Array.isArray(notification.metrics)
                                      ? notification.metrics?.find(
                                          (matrix) => matrix[metricEntryA[0]]
                                        )
                                      : notification.metrics;
                                    const matchedMetricsB = Array.isArray(notification.metrics)
                                      ? notification.metrics?.find(
                                          (matrix) => matrix[metricEntryA[0]]
                                        )
                                      : notification.metrics;
                                    const orderOfA =
                                      matchedMetricsA[metricEntryA[0]]?.order ||
                                      matchedMetricsA[metricEntryA[0]].length;
                                    const orderOfB =
                                      matchedMetricsB[metricEntryB[0]]?.order ||
                                      matchedMetricsB[metricEntryB[0]].length;
                                    if (orderOfA === orderOfB) return 0;
                                    if (orderOfA < orderOfB) {
                                      return -1;
                                    }
                                    return 1;
                                  })
                                  .map((metricEntry, metricEntryIndex) => {
                                    const metricEntryValidations = setting.field_value[
                                      notificationIndex
                                    ].active
                                      ? metricsValidations?.[metricEntry[0]] || {}
                                      : {
                                          required: false,
                                          validate: () => true,
                                        };

                                    if (!setting.field_value[notificationIndex].active) {
                                      if (metricEntryValidations?.requiredWhen) {
                                        const requiredStr = metricEntryValidations.required;
                                        metricEntryValidations.validate = (val) => {
                                          if (!setting.field_value[notificationIndex].active) {
                                            return true;
                                          }

                                          if (!val) return requiredStr || "Field is required";
                                          if (
                                            metricEntryValidations?.type === "number" &&
                                            Number.isNaN(Number(val))
                                          )
                                            return "Must be a number";
                                          return true;
                                        };
                                        delete metricEntryValidations.required;
                                      }

                                      if (metricEntryValidations?.length) {
                                        metricEntryValidations.validate = (val) => {
                                          if (!val || val?.length < metricEntryValidations.length)
                                            return (
                                              metricEntryValidations.message ||
                                              "Please select at least 1 option"
                                            );

                                          return true;
                                        };
                                        delete metricEntryValidations.message;
                                      }
                                    }

                                    const matchedMetrics = Array.isArray(notification.metrics)
                                      ? notification.metrics?.find(
                                          (matrix) => matrix[metricEntry[0]]
                                        )
                                      : notification.metrics;
                                    const componentTypeKey = matchedMetrics[metricEntry[0]].type;
                                    const MetricComponent =
                                      componentTypeMap[componentTypeKey].Component;
                                    const options =
                                      // eslint-disable-next-line no-nested-ternary
                                      metricEntry[0] === "recipientList"
                                        ? notification.recipientListValues[
                                            setting.field_value[notificationIndex].notificationRows[
                                              index
                                            ].metrics[i].recipientType.selectedValue
                                          ]
                                        : metricEntry[0] === "recipientType"
                                        ? notification.notificationRecipientTypeValues
                                        : matchedMetrics[metricEntry[0]].picklistValues || [];

                                    const formattedFieldValue =
                                      // eslint-disable-next-line no-nested-ternary
                                      componentTypeKey === "single-select-dropdown"
                                        ? {
                                            value: metricEntry[1].selectedValue,
                                            display:
                                              options.find(
                                                (option) =>
                                                  option.value === metricEntry[1].selectedValue
                                              )?.display || metricEntry[1].selectedValue,
                                          }
                                        : componentTypeKey === "multi-select-dropdown"
                                        ? metricEntry[1].selectedValue?.map((entry) => ({
                                            value: entry,
                                            display:
                                              options.find((option) => option.value === entry)
                                                ?.display || entry,
                                          })) || metricEntry[1].selectedValue
                                        : metricEntry[1].selectedValue;

                                    return (
                                      <>
                                        <Grid
                                          // eslint-disable-next-line react/no-array-index-key
                                          key={`metric-entries-${notification.category}-${row.rowId}-${metricEntryIndex}`}
                                          item
                                          xs={
                                            notification.notificationRows[index].metrics.length >
                                              1 && metricEntryIndex === 1
                                              ? 5
                                              : 5.5
                                          }
                                        >
                                          <MetricComponent
                                            name={`${notification.category}-${row.rowId}-${metricEntry[0]}-${i}-${metricEntryIndex}`}
                                            setting={{
                                              field_value: formattedFieldValue,
                                              en_field_label: metricEntry[0],
                                              options,
                                            }}
                                            setParentState={(newSelectedValue) => {
                                              const copy = cloneDeep(setting.field_value);
                                              copy[notificationIndex].notificationRows[
                                                index
                                              ].metrics[i][metricEntry[0]].selectedValue =
                                                Array.isArray(newSelectedValue)
                                                  ? newSelectedValue.map(
                                                      (value) => value?.value || value
                                                    )
                                                  : newSelectedValue?.value || newSelectedValue;

                                              if (metricEntry[0] === "recipientType") {
                                                copy[notificationIndex].notificationRows[
                                                  index
                                                ].metrics[i].recipientList.selectedValue = [];
                                              }

                                              setParentState(copy);
                                            }}
                                            validations={metricEntryValidations}
                                          />
                                        </Grid>
                                        {notification.notificationRows[index].metrics.length > 1 &&
                                        metricEntryIndex === 1 ? (
                                          <Grid item xs={1}>
                                            <IconButton
                                              size="large"
                                              color="inherit"
                                              variant="contained"
                                              onClick={() => {
                                                const copy = cloneDeep(setting.field_value);
                                                copy[notificationIndex].notificationRows[
                                                  index
                                                ].metrics.splice(i, 1);
                                                setParentState(copy);
                                                clearErrors();
                                              }}
                                            >
                                              <Icon fontSize="medium">delete</Icon>
                                            </IconButton>
                                          </Grid>
                                        ) : null}
                                      </>
                                    );
                                  })}
                              </Grid>
                            );
                          })}

                          <MDButton
                            onClick={() => {
                              const copy = cloneDeep(setting.field_value);
                              copy[notificationIndex].notificationRows[index].metrics.push(
                                Object.keys(
                                  setting.default_field_value.category[notification?.category]
                                    .notificationRows[0].metrics[0]
                                ).reduce((result, key) => {
                                  result[key] = {
                                    selectedValue:
                                      setting.default_field_value.category[notification?.category]
                                        .notificationRows[0].metrics[0][key],
                                  };
                                  return result;
                                }, {})
                              );
                              setParentState(copy);
                            }}
                            variant="gradient"
                            color="newColor"
                          >
                            Add another row
                          </MDButton>
                        </Box>
                      ) : (
                        !!metricEntries.length && (
                          <Grid
                            container
                            justifyContent="space-between"
                            sx={{
                              width: "100%",
                            }}
                          >
                            {metricEntries
                              .sort((metricEntryA, metricEntryB) => {
                                const orderOfA =
                                  notification.metrics[metricEntryA[0]]?.order ||
                                  notification.metrics[metricEntryA[0]].length;
                                const orderOfB =
                                  notification.metrics[metricEntryB[0]]?.order ||
                                  notification.metrics[metricEntryB[0]].length;
                                if (orderOfA === orderOfB) return 0;
                                if (orderOfA < orderOfB) {
                                  return -1;
                                }
                                return 1;
                              })
                              .map((metricEntry) => {
                                const nestedMetricsRow = notification.metrics[metricEntry[0]];
                                const nestedRow = metricEntry[1].selectedValue;
                                const componentTypeKey = nestedMetricsRow.type;

                                if (componentTypeKey === "nested-metrics") {
                                  return (
                                    <NestedMetrics
                                      key={`${notification.category}-${row.rowId}-${metricEntry[0]}`}
                                      uniqueKey={`${notification.category}-${row.rowId}-${metricEntry[0]}`}
                                      nestedMetricsRow={nestedMetricsRow}
                                      nestedRow={nestedRow}
                                      metricEntry={metricEntry}
                                      notification={notification}
                                      notificationIndex={notificationIndex}
                                      setting={setting}
                                      index={index}
                                      validations={validations}
                                      setParentState={setParentState}
                                      clearErrors={clearErrors}
                                      metricsPath={[
                                        notificationIndex,
                                        "metrics",
                                        metricEntry[0],
                                        "metrics",
                                      ]}
                                      stateUpdatePath={[
                                        notificationIndex,
                                        "notificationRows",
                                        index,
                                        "metrics",
                                        metricEntry[0],
                                        "selectedValue",
                                      ]}
                                    />
                                  );
                                }

                                const MetricComponent =
                                  componentTypeMap[componentTypeKey].Component;
                                const options =
                                  notification.metrics[metricEntry[0]].picklistValues || [];
                                const formattedFieldValue =
                                  componentTypeKey === "single-select-dropdown"
                                    ? {
                                        value: metricEntry[1].selectedValue,
                                        display: metricEntry[1].selectedValue,
                                      }
                                    : metricEntry[1].selectedValue;

                                return (
                                  <Grid key={`metric-entries${metricEntry?.[0]}`} item xs={5.5}>
                                    <MetricComponent
                                      setting={{
                                        field_value: formattedFieldValue,
                                        en_field_label: metricEntry[0],
                                        options,
                                      }}
                                      setParentState={(newSelectedValue) => {
                                        const copy = cloneDeep(setting.field_value);
                                        copy[notificationIndex].notificationRows[index].metrics[
                                          metricEntry[0]
                                        ].selectedValue = newSelectedValue;
                                        setParentState(copy);
                                      }}
                                      name={`metric-entries${metricEntry?.[0]}`}
                                      validations={{
                                        validate: (value) =>
                                          setting.field_value[notificationIndex].active &&
                                          value?.length < validations?.recipientList?.length
                                            ? validations?.recipientList?.message
                                            : true,
                                      }}
                                    />
                                  </Grid>
                                );
                              })}
                          </Grid>
                        )
                      )}
                    </Box>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

export default NotificationBuilderSetting;
